
import { Observable, ReplaySubject, from, merge, of, zip } from "rxjs";
import { catchError, filter, first, map, mergeMap, switchMap, take } from "rxjs/operators";
import _ from "lodash";

import { AssociatedMediasResource, ReplaySubjectContent, SynchroMediaUpdates, PushCachedValueResult, MEDIA_SUBJECTS_KEYS, MediaType, EntityType } from "@skinsoft/types";
import { AbstractSyncUpHandler } from "../abstract";
import { OfflineDataUpdateCacher } from "@skinsoft/offline";
import { dbCacheUp } from "@skinsoft/offline/dexie/cache-up";
import { DataStore } from "@skinsoft/data-store";
import { ImageTransferHelper, MediaTransferHelper } from "@skinsoft/media-transfer-helper";
import { mergeResource } from "@skinsoft/utils";
import { SynchroUpIdMapping } from "@skinsoft/synchro-up-mapping";
import { SyncUpDependency } from "@skinsoft/classes-interfaces-definition";

declare const config
const NB_TRY_FETCH_MEDIA_SYNC_UP: number = config.syncUp.medias.maxNbRetry
const INTERVAL_FETCH_MEDIA_SYNC_UP: number = config.syncUp.medias.delayBetweenRetry

/**
 * @type T type of entity stored inside CACHE-UP
 * @type U type of entity pushed to backend
 * @type A type of after push additionnalData defaults to any
 */
export abstract class EntitySyncUpHandler<T extends { id: string }, U extends { id: string }, A = any> extends AbstractSyncUpHandler<T, U, A> {
    protected offlineDataUpdateCacher: OfflineDataUpdateCacher<T>
    protected dataStore: DataStore<U>
    protected type: EntityType
    protected synchronizationMap: {[key: string]: string} = {}
    protected imageCache = dbCacheUp.media

    protected synchroUpIdMapping: SynchroUpIdMapping

    constructor(offlineDataUpdateCacher: OfflineDataUpdateCacher<T>, dataStore: DataStore<U>, type: EntityType) {
        super()
        this.offlineDataUpdateCacher = offlineDataUpdateCacher
        this.dataStore = dataStore
        this.type = type

        this.synchroUpIdMapping = SynchroUpIdMapping.getInstance()
    }

    getNbCachedObjects(countLocallyCreated: boolean = true): Promise<number> {
        return this.offlineDataUpdateCacher.getNbCachedObjects(countLocallyCreated)
    }
    getNbLocallyCreatedEntities(): Promise<number> {
        return this.offlineDataUpdateCacher.getNbCachedCreations()
    }

    getCachedObjectsIds(): Observable<string[]> {
        return from(
            this.offlineDataUpdateCacher.getCachedObjectsKeys(true, false)
        )
    }

    getCachedEntity(key: string): Observable<T> {
        return from(
            this.offlineDataUpdateCacher.fetchOneFromId(key)
        ) as Observable<T>
    }

    getDependenciesIds(id: string): Observable<SyncUpDependency[]> {
        return this.getCachedEntity(id).pipe(
            map(entity => {
                return this.getCachedAssociatedMediasIds(entity)
            })
        )
    }

    getCachedAssociatedMediasIds(entity: T): SyncUpDependency[] {
        const res: SyncUpDependency[] = []

        if((entity as any).associatedMedias) {
            const entityNoType: any = entity

            if(entityNoType.associatedMedias?.first && MediaTransferHelper.isLocalMedia(entityNoType.associatedMedias.first)) {
                res.push({
                    id: entityNoType.associatedMedias.first,
                    type: EntityType.MEDIA,
                })
            }

            if(entityNoType.associatedMedias?.second && MediaTransferHelper.isLocalMedia(entityNoType.associatedMedias.second)) {
                res.push({
                    id: entityNoType.associatedMedias.first,
                    type: EntityType.MEDIA,
                })
            }

            if(entityNoType.associatedMedias?.third && MediaTransferHelper.isLocalMedia(entityNoType.associatedMedias.third)) {
                res.push({
                    id: entityNoType.associatedMedias.first,
                    type: EntityType.MEDIA,
                })
            }

            if(entityNoType.associatedMedias?.medias?.length) {
                for(const mediaId of entityNoType.associatedMedias.medias) {
                    if(MediaTransferHelper.isLocalMedia(mediaId)) {
                        res.push({
                            id: mediaId,
                            type: EntityType.MEDIA,
                        })
                    }
                }
            }
        }

        return _.uniqBy(res, 'id')
    }

    updateCachedAssociatedMediasIds(entity: T): Observable<T> {
        if((entity as any).associatedMedias) {
            return of(null).pipe(
                switchMap(async () => {
                    const entityNoType: any = entity
                    const updatedEntity = _.cloneDeep(entity)
        
                    if(entityNoType.associatedMedias?.first && MediaTransferHelper.isLocalMedia(entityNoType.associatedMedias.first)) {
                        const newId = await this.synchroUpIdMapping.getNewId(entityNoType.associatedMedias.first)
                        if(!newId) {
                            throw new Error()
                        }
        
                        _.set(
                            updatedEntity as unknown as object,
                            'associatedMedias.first',
                            newId
                        )
                    }
        
                    if(entityNoType.associatedMedias?.second && MediaTransferHelper.isLocalMedia(entityNoType.associatedMedias.second)) {
                        const newId = await this.synchroUpIdMapping.getNewId(entityNoType.associatedMedias.second)
                        if(!newId) {
                            throw new Error()
                        }
                        
                        _.set(
                            updatedEntity as unknown as object,
                            'associatedMedias.second',
                            newId
                        )
                    }
        
                    if(entityNoType.associatedMedias?.third && MediaTransferHelper.isLocalMedia(entityNoType.associatedMedias.third)) {
                        const newId = await this.synchroUpIdMapping.getNewId(entityNoType.associatedMedias.third)
                        if(!newId) {
                            throw new Error()
                        }
                        
                        _.set(
                            updatedEntity as unknown as object,
                            'associatedMedias.third',
                            newId
                        )
                    }
        
                    if(entityNoType.associatedMedias?.medias?.length) {
                        const newMedias = await Promise.all(
                            entityNoType.associatedMedias.medias.map(async mediaId => {
                                if(MediaTransferHelper.isLocalMedia(mediaId)) {
                                    const newId = await this.synchroUpIdMapping.getNewId(mediaId)
                                    if(!newId) {
                                        throw new Error()
                                    }
                                    return newId
                                }
            
                                return mediaId
                            })
                        )
        
                        _.set(
                            updatedEntity as unknown as object,
                            'associatedMedias.medias',
                            newMedias
                        )
                    }
        
                    return updatedEntity
                })
            )

        }

        return of(entity)
    }

    getAllCachedEntities(includeCashedCreations: boolean = true): Observable<T[]> {
        return from(
            this.offlineDataUpdateCacher.getAllRealObjects(includeCashedCreations) as Promise<T[]>
        )
    }

    formatDataBeforePush(entity: T): Observable<U> {
        return this.updateCachedAssociatedMediasIds(entity) as unknown as Observable<U>
    }

    removeLocalMedias(entity: U): U {
        let cleanedEntity = {...entity}
        if((entity as any).associatedMedias) {
            const associatedMedias = (entity as any).associatedMedias

            if(associatedMedias?.first && MediaTransferHelper.isLocalMedia(associatedMedias?.first)) {
                _.set(cleanedEntity, 'associatedMedias.first', undefined)
            }
            if(associatedMedias?.second && MediaTransferHelper.isLocalMedia(associatedMedias?.second)) {
                _.set(cleanedEntity, 'associatedMedias.second', undefined)
            }
            if(associatedMedias?.third && MediaTransferHelper.isLocalMedia(associatedMedias?.third)) {
                _.set(cleanedEntity, 'associatedMedias.third', undefined)
            }

            if(associatedMedias.medias?.length) {
                (cleanedEntity as any).associatedMedias.medias = (cleanedEntity as any).associatedMedias.medias
                    .filter(mediaId => {
                        return !MediaTransferHelper.isLocalMedia(mediaId)
                    })
            }
        }

        return cleanedEntity
    }

    getEntityId(entity: U): string {
        return entity.id
    }

    getLocalAssociatedMedias(entity: U): AssociatedMediasResource {
        let localAssociatedMedias: AssociatedMediasResource = {}
        const entityAssociatedMedias = (entity as any).associatedMedias

        if(entityAssociatedMedias?.medias?.length) {
            let localMedias = entityAssociatedMedias.medias.filter(mediaId => {
                return MediaTransferHelper.isLocalMedia(mediaId)
            })

            localAssociatedMedias.medias = localMedias ?? undefined // assign undefined in case localMedias == null

        }
        if(entityAssociatedMedias?.first) {
            if(MediaTransferHelper.isLocalMedia(entityAssociatedMedias.first)) {
                localAssociatedMedias.first = entityAssociatedMedias.first
            }
        }
        if(entityAssociatedMedias?.second) {
            if(MediaTransferHelper.isLocalMedia(entityAssociatedMedias.second)) {
                localAssociatedMedias.second = entityAssociatedMedias.second
            }
        }
        if(entityAssociatedMedias?.third) {
            if(MediaTransferHelper.isLocalMedia(entityAssociatedMedias.third)) {
                localAssociatedMedias.third = entityAssociatedMedias.third
            }
        }

        return localAssociatedMedias
    }

    getLocalMediasKeys(entity: U): string[] | null {
        if((entity as any).associatedMedias) {
            const associatedMedias = (entity as any).associatedMedias
            let localMediasKeys: string[] = []

            if(associatedMedias.first) {
                if(MediaTransferHelper.isLocalMedia(associatedMedias.first)) {
                    localMediasKeys.push(associatedMedias.first)
                }
            }
            if(associatedMedias.second) {
                if(MediaTransferHelper.isLocalMedia(associatedMedias.second)) {
                    localMediasKeys.push(associatedMedias.second)
                }
            }
            if(associatedMedias.third) {
                if(MediaTransferHelper.isLocalMedia(associatedMedias.third)) {
                    localMediasKeys.push(associatedMedias.third)
                }
            }

            if(associatedMedias.medias?.length) {
                localMediasKeys = localMediasKeys.concat(
                    associatedMedias.medias.filter(mediaId => {
                        return MediaTransferHelper.isLocalMedia(mediaId)
                    })
                )
            }

            return _.uniq(localMediasKeys)
        }
        return null
    }


    buildUploadCachedMediasReplaySubject(localMediasKeys: string[] | null): { [replayName: string]: ReplaySubject<ReplaySubjectContent>; } {
        let mediaSubjects: { [replayName: string]: ReplaySubject<ReplaySubjectContent> } = {}
        
        if (localMediasKeys?.length) {
            mediaSubjects[MEDIA_SUBJECTS_KEYS.image] = new ReplaySubject()
            const mediaTransferHelper = MediaTransferHelper.getInstance()
            
            const localImages = localMediasKeys.filter(mediaKey => mediaTransferHelper.getMediaType(mediaKey) === MediaType.IMAGE)
            
            const imageTransferHelper = ImageTransferHelper.getInstance()
            const mediaObservable = merge(from(localImages)).pipe(
                mergeMap((localImageKey) => {
                    return from(this.imageCache.get(localImageKey)).pipe(
                        map(localImageValue => localImageValue as unknown as (string | File)),
                        map(localImageValue => {
                            if(localImageValue instanceof File) {
                                return localImageValue
                            } else {
                                return mediaTransferHelper.convertBase64ToFile(localImageValue, 'image/jpg')
                            }
                        }),
                        map(blob => imageTransferHelper.uploadImage('image/jpg', new File([blob], `temp.${blob.type.split('/').pop()}`), 'data:')),
                        map((uploadResult): ReplaySubjectContent => {
                            return {
                                cacheKeys: [uploadResult.cacheKey, localImageKey]
                            }
                        }),
                        catchError(err => of({
                            error: err
                        } as ReplaySubjectContent)),
                    )
                }),
                mergeMap(uploadRes => {
                    if(!uploadRes.error && uploadRes.fileName) {
                        return from(
                            imageTransferHelper.repeatFindingMediaIdFromFileNameWithLimit(
                                uploadRes.fileName,
                                NB_TRY_FETCH_MEDIA_SYNC_UP,
                                INTERVAL_FETCH_MEDIA_SYNC_UP
                            )
                        ).pipe(
                            map(media => {
                                if(media.id) {
                                    const result = _.cloneDeep(uploadRes)
                                    result.newMediaId = media.id
                                    return result
                                }

                                throw new Error("Failed to find media")
                            }),
                        )
                    }
                    
                    return of(uploadRes)
                }),
                filter(downloadRes => !!downloadRes.newMediaId),
                take(localImages.length),
            )

            mediaObservable.subscribe(mediaSubjects[MEDIA_SUBJECTS_KEYS.image])
        }

        return mediaSubjects
    }


    applyBeforePushOperations(dataToPush: SynchroMediaUpdates<U>): Observable<[PushCachedValueResult, SynchroMediaUpdates<U>]> {
        return of([
            { success: true } as PushCachedValueResult,
            dataToPush
        ])
    }

    isCachedCreation(id: string): boolean {
        return OfflineDataUpdateCacher.isTemporaryId(id) as boolean
    }

    getOriginalEntity(id: string): Observable<U> {
        return this.dataStore.fetchOneOnlineMode(id)
    }

    createNewEntity(dataToCreate: SynchroMediaUpdates<U>): Observable<U> {
        return this.dataStore.create()
    }
    
    abstract getAndFormatModificationsBeforeMerge(localEntity: U): Observable<Partial<U>>
    mergeModificationsInOriginalEntity(original: U, modification: Partial<U>): Observable<U> {
        return of(
            mergeResource(original, modification)
        )
    }

    pushCachedUpdate(dataToUpdate: SynchroMediaUpdates<U>): Observable<PushCachedValueResult> {
        if (dataToUpdate.id && dataToUpdate.body) {
            // Delete updated date (used locally but has to be set by the api)
            // @ts-ignore
            delete dataToUpdate.body.managementData?.updatedDate

            return this.getOriginalEntity(dataToUpdate.id).pipe(
                switchMap(originalEntity => {
                    return zip(
                        of(originalEntity),
                        this.getAndFormatModificationsBeforeMerge(dataToUpdate.body)
                    )
                }),
                switchMap(([originalEntity, modifications]) => {
                    return this.mergeModificationsInOriginalEntity(originalEntity, modifications)
                }),
                switchMap(mergedEntity => {
                    return this.dataStore.update(
                        dataToUpdate.id,
                        mergedEntity,
                        undefined,
                        true
                    )
                }),
                first(),
                // Handle success or error if the update and emit boolean
                catchError(e => of(Error('Error during update...'))),
                switchMap(
                    response => {
                        if (!(response instanceof Error)) {
                            return of({
                                success: true
                            } as PushCachedValueResult)
                        }
                        return of({
                            success: false
                        } as PushCachedValueResult)
                    }
                )                
            )
        }
        else {
            return of({
                success: false
            } as PushCachedValueResult)
        }
    }

    pushCachedCreations(dataToCreate: SynchroMediaUpdates<U>): Observable<PushCachedValueResult> {
        if (dataToCreate.id && dataToCreate.body) {
            // Delete management data dates (has to be set by the API on the official creation of the item)
            // @ts-ignore
            delete dataToCreate.body.managementData?.creationDate
            // @ts-ignore
            delete dataToCreate.body.managementData?.updatedDate

            this.saveMappingOldIdNewId(dataToCreate.id, 'inProgress')
            return this.createNewEntity(dataToCreate).pipe(
                switchMap(createdEntity => {
                    this.saveMappingOldIdNewId(dataToCreate.id, createdEntity.id)

                    return zip(
                        of(createdEntity),
                        this.getAndFormatModificationsBeforeMerge(dataToCreate.body)
                    )
                }),
                switchMap(([createdEntity, modifications]) => {
                    return this.mergeModificationsInOriginalEntity(createdEntity, modifications)
                }),
                switchMap(mergedEntity => {
                    return this.dataStore.update(mergedEntity.id, mergedEntity)
                }),
                first(),
                // Handle success or error if the update and emit boolean
                catchError(err => {
                    return of(Error('Error during update...'))
                }),
                switchMap(
                    response => {
                        if (!(response instanceof Error)) {
                            const newId = (response as unknown as {id: string}).id
                            return of({
                                success: true,
                                newId: newId
                            } as PushCachedValueResult)
                        }
                        return of({
                            success: false
                        } as PushCachedValueResult)
                    }
                )
            )
        } else {
            return of({
                success: false,
            } as PushCachedValueResult)
        }
    }

    saveMappingOldIdNewId(oldId: string, newId: string): void {
        this.synchronizationMap[oldId] = newId
    }

    getAfterPushAdditionalData(dataToPush: SynchroMediaUpdates<U>): Observable<A> {
        return of(undefined as any)
    }

    applyAfterPushOperations(pushResult: PushCachedValueResult, dataPushed: SynchroMediaUpdates<U>, additionalData?: A): Observable<PushCachedValueResult> {
        return of(pushResult)
    }

    cleanCacheAfterPush(cacheKeys: string[]): void {
        for(const key of cacheKeys) {
            this.offlineDataUpdateCacher.removeCachedValue(key)
        }
    }
}